import { IconMapPin, IconCreditCard } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { IconPill } from "../../../../components/common/icon-pill";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import type { SaleDetailsDto } from "../../../../api";
import { dollarFormat } from "../../../../utils/currencies";

interface OrderSidebarProps {
  sale: SaleDetailsDto;
  showInternalCosts?: boolean;
}

export function OrderSidebar({ sale, showInternalCosts = false }: OrderSidebarProps) {
  const currency = sale.currency?.name ?? "QAR";
  const shippingPartner = sale.shippingPartner ?? sale.partner;
  const shipmentEstimate = (sale.dropshipping ?? []).reduce(
    (sum, shipment) => sum + Number(shipment.internalDeliveryCostEstimate ?? 0),
    0,
  );
  const shipmentActual = (sale.dropshipping ?? []).reduce(
    (sum, shipment) => sum + Number(shipment.internalDeliveryActualCost ?? 0),
    0,
  );
  const shipmentCount = sale.dropshipping?.length ?? 0;
  const internalCurrency =
    sale.dropshipping?.find((shipment) => shipment.internalDeliveryActualCostCurrency)?.internalDeliveryActualCostCurrency
    ?? sale.dropshipping?.find((shipment) => shipment.internalDeliveryCostCurrency)?.internalDeliveryCostCurrency
    ?? "QAR";

  return (
    <div className="flex flex-col gap-4">
      <OrderSummaryCard sale={sale} currency={currency} />
      {showInternalCosts && shipmentCount > 0 && (
        <SidebarCard icon={<IconCreditCard size={18} />} title="Internal Delivery Cost">
          <div className="flex flex-col gap-2">
            <SummaryRow label="Shipment count">
              <Text variant="bodyBold" tone="dark">
                {shipmentCount.toLocaleString()}
              </Text>
            </SummaryRow>
            <SummaryRow label="Estimated">
              <Text variant="bodyBold" tone="dark">
                {dollarFormat(shipmentEstimate, internalCurrency)}
              </Text>
            </SummaryRow>
            <SummaryRow label="Actual recorded">
              <Text variant="bodyBold" tone="dark">
                {dollarFormat(shipmentActual, internalCurrency)}
              </Text>
            </SummaryRow>
            <SummaryRow label="Variance">
              <Text
                variant="bodyBold"
                tone="inherit"
                className={shipmentActual > shipmentEstimate ? "text-accentError" : "text-accentGreenDark"}
              >
                {dollarFormat(shipmentActual - shipmentEstimate, internalCurrency)}
              </Text>
            </SummaryRow>
          </div>
        </SidebarCard>
      )}
      {shippingPartner && (
        <SidebarCard icon={<IconMapPin size={18} />} title="Delivery Address">
          <div className="flex flex-col gap-0.5">
            {shippingPartner.name && (
              <Text variant="bodyBold" tone="dark">{shippingPartner.name}</Text>
            )}
            {shippingPartner.street && (
              <Text variant="bodySm" tone="body">{shippingPartner.street}</Text>
            )}
            {(shippingPartner.city || shippingPartner.zip) && (
              <Text variant="bodySm" tone="body">
                {[shippingPartner.zip, shippingPartner.city].filter(Boolean).join(" ")}
              </Text>
            )}
            {shippingPartner.country?.name && (
              <Text variant="bodySm" tone="body">{shippingPartner.country.name}</Text>
            )}
            {shippingPartner.phone && (
              <Text variant="bodySm" tone="body">Contact: {shippingPartner.phone}</Text>
            )}
          </div>
        </SidebarCard>
      )}

      <SidebarCard icon={<IconCreditCard size={18} />} title="Payment Status">
        <div className="flex items-center justify-between gap-3">
          <Text variant="bodyBold" tone="dark">{formatInvoiceStatus(sale.invoiceStatus)}</Text>
          {sale.invoicePreviewUrl && (
            <Button
              variant="ghostAccent"
              size="sm"
              onClick={() =>
                window.open(sale.invoicePreviewUrl!, "_blank", "noopener,noreferrer")
              }
            >
              View invoice
            </Button>
          )}
        </div>
      </SidebarCard>
    </div>
  );
}

function OrderSummaryCard({ sale, currency }: { sale: SaleDetailsDto; currency: string }) {
  return (
    <div className="flex flex-col gap-3">
      <Heading variant="h3" tone="dark">Order Summary</Heading>
      <div className="flex flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
      <div className="flex flex-col gap-2">
        <SummaryRow label="Subtotal">
          <CurrencyAmount amount={sale.amountUntaxed} currency={currency} variant="bodyBold" tone="dark" />
        </SummaryRow>
        <SummaryRow label="VAT">
          <CurrencyAmount amount={sale.amountTax} currency={currency} variant="bodyBold" tone="dark" />
        </SummaryRow>
        <div className="flex items-center justify-between">
          <Text variant="body" tone="body">Delivery</Text>
          <Text variant="bodyBold" tone="greenDark">Included</Text>
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-borderLight pt-3">
        <Text variant="bodyBold" tone="dark">Total</Text>
        <CurrencyAmount amount={sale.amountTotal} currency={currency} variant="h2" tone="dark" />
      </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <Text variant="body" tone="body">{label}</Text>
      {children}
    </div>
  );
}

function SidebarCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
      <div className="flex items-center gap-2">
        <IconPill size={36} tone="ghost" shape="square">{icon}</IconPill>
        <Heading variant="h3" tone="dark">{title}</Heading>
      </div>
      {children}
    </div>
  );
}

function formatInvoiceStatus(value: string | null | undefined): string {
  const v = (value ?? "").toLowerCase();
  if (v === "invoiced") return "Invoiced";
  if (v === "to invoice" || v === "to_invoice") return "Pending invoice";
  if (v === "no") return "Not yet invoiced";
  if (!v) return "—";
  return value!;
}
